import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AcademyStats from "@/components/academy/AcademyStats";
import CourseCreationForm from "@/components/academy/CourseCreationForm";
import CoursesTable from "@/components/academy/CoursesTable";
import CourseEditDialog from "@/components/academy/CourseEditDialog";
import AvailabilityTab from "@/components/mentor/AvailabilityTab";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Course } from "@/types/course";

const MyAcademy = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userName, setUserName] = useState("User");
  const [approvedCourses, setApprovedCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("courses");
  const { toast } = useToast();
  const { user } = useAuth();

  // Check for tab parameter in URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'availability') {
      setActiveTab('availability');
    }
  }, [searchParams]);

  const loadApprovedCourses = useCallback(async () => {
    console.log("Loading all courses for user:", user?.id);
    if (!user?.id) {
      console.log("No user ID found, skipping courses fetch");
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Check authentication session first
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      console.log("Current auth session:", sessionData?.session?.user?.id);
      
      if (!sessionData?.session) {
        console.error("No valid session found, attempting to refresh");
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData?.session) {
          console.error("Session refresh failed:", refreshError);
          toast({
            title: "Authentication Error",
            description: "Please log in again to view your courses.",
            variant: "destructive",
          });
          navigate("/login");
          return;
        }
        console.log("Session refreshed successfully");
      }

      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('mentor_id', user.id)
        .order('created_at', { ascending: false });
      
      console.log("Supabase courses response:", { data, error });
      
      if (error) {
        console.error("Error loading courses:", error);
        // Check if it's an auth-related error
        if (error.message?.includes('JWT') || error.message?.includes('authentication') || error.code === 'PGRST301') {
          console.error("Authentication error detected:", error);
          toast({
            title: "Authentication Error", 
            description: "Please log in again to view your courses.",
            variant: "destructive",
          });
          navigate("/login");
        } else {
          toast({
            title: "Error",
            description: "Failed to load courses. Please try again.",
            variant: "destructive",
          });
        }
        setApprovedCourses([]);
        setIsLoading(false);
        return;
      }
      
      if (data && Array.isArray(data)) {
        console.log("Found courses:", data.length);
        const mappedCourses: Course[] = data
          .filter(course => course !== null)
          .map(course => {
            let courseType: 'subscription' | 'call';
            if (course.course_type === 'subscription') {
              courseType = 'subscription';
            } else {
              courseType = 'call';
            }

            return {
              id: course.id,
              title: course.title,
              description: course.description || '',
              courseType,
              price: course.price,
              duration: course.duration,
              mentorId: course.mentor_id,
              status: course.status as 'pending' | 'approved' | 'rejected',
              createdAt: course.created_at,
              updatedAt: course.updated_at,
              author: 'Me',
              instructor: 'Me',
              category: course.course_type === 'subscription' ? 
                (course.subscription_type === 'monthly' ? 'Monthly Subscription' : 'Weekly Subscription') : 
                'Voice/Video Call',
              rejectionReason: course.rejection_reason,
              subscriptionType: course.subscription_type as 'weekly' | 'monthly',
              isActive: course.is_active !== false
            };
          });
        console.log("Mapped courses:", mappedCourses);
        setApprovedCourses(mappedCourses);
      } else {
        console.log("No courses found or data is not an array");
        setApprovedCourses([]);
      }
    } catch (error) {
      console.error("Error in loadCourses:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading courses.",
        variant: "destructive",
      });
      setApprovedCourses([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, navigate]);

  // Set up real-time subscription for courses
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('courses-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'courses',
        filter: `mentor_id=eq.${user.id}`
      }, (payload) => {
        console.log('Course change detected:', payload);
        // Reload courses when any change occurs
        loadApprovedCourses();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, loadApprovedCourses]);

  useEffect(() => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        if (parsed.firstName) {
          setUserName(parsed.firstName);
        }
        
        if (parsed.profileType !== "mentor" && parsed.profileType !== "both") {
          navigate("/dashboard");
        }
        
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    } else {
      navigate("/login");
    }
    
    if (user?.id) {
      loadApprovedCourses();
    }
  }, [navigate, user, loadApprovedCourses]);

  const handleToggleActive = async (courseId: string, courseTitle: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    
    try {
      const { error } = await supabase
        .from('courses')
        .update({ is_active: newStatus })
        .eq('id', courseId);

      if (error) {
        throw error;
      }

      toast({
        title: newStatus ? "Course Activated" : "Course Deactivated",
        description: `"${courseTitle}" has been ${newStatus ? 'activated and will appear on your profile' : 'deactivated and removed from your profile'}.`,
        variant: "default",
      });

      // Reload courses to reflect the change
      loadApprovedCourses();
    } catch (error: any) {
      console.error('Error toggling course status:', error);
      toast({
        title: "Error",
        description: `Failed to ${newStatus ? 'activate' : 'deactivate'} the course: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleDeleteCourse = async (courseId: string, courseTitle: string) => {
    try {
      // Check applications and their statuses
      const { data: applications, error: checkError } = await supabase
        .from("applications")
        .select("id, status")
        .eq("course_id", courseId);

      if (checkError) {
        console.error("Error checking applications:", checkError);
        throw checkError;
      }

      if (applications && applications.length > 0) {
        // Check if all applications are completed
        const nonCompletedApplications = applications.filter(app => app.status !== 'completed');
        
        if (nonCompletedApplications.length > 0) {
          const totalApplications = applications.length;
          const completedApplications = totalApplications - nonCompletedApplications.length;
          
          toast({
            title: "Cannot Delete Course",
            description: `This course has ${nonCompletedApplications.length} active application(s) that are not completed. Only ${completedApplications} out of ${totalApplications} applications are completed. You can only delete courses where all applications are completed.`,
            variant: "destructive",
          });
          return;
        }
      }

      // All applications are completed or no applications exist, proceed with deletion
      // Note: We only delete the course, not the applications or reviews
      const { error } = await supabase
        .from("courses")
        .delete()
        .eq("id", courseId)
        .eq("mentor_id", user?.id);

      if (error) throw error;

      toast({
        title: "Course Deleted",
        description: `"${courseTitle}" has been deleted successfully. All application and review history has been preserved.`,
      });

      // Explicitly reload courses as a fallback to real-time subscription
      console.log("Course deleted successfully, reloading courses list");
      await loadApprovedCourses();
    } catch (error) {
      console.error("Error deleting course:", error);
      toast({
        title: "Error",
        description: "Unable to delete course. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResubmitCourse = async (courseId: string, courseTitle: string) => {
    try {
      // Update the course status to pending and clear rejection reason
      const { error: updateError } = await supabase
        .from("courses")
        .update({
          status: "pending",
          rejection_reason: null,
          updated_at: new Date().toISOString()
        })
        .eq("id", courseId)
        .eq("mentor_id", user?.id);

      if (updateError) throw updateError;

      // Create a notification for admin review
      const { error: notificationError } = await supabase
        .from("notifications")
        .insert({
          receiver_id: user?.id, // This would typically be an admin user ID
          type: "course_resubmitted",
          content: `Course "${courseTitle}" has been resubmitted for approval by ${userName}`,
          link: `/admin/courses/${courseId}`
        });

      if (notificationError) {
        console.error("Error creating notification:", notificationError);
        // Don't throw here as the main action (resubmitting) was successful
      }

      toast({
        title: "Course Resubmitted",
        description: "Course resubmitted for approval.",
      });

      // Reload courses to reflect the updated status
      await loadApprovedCourses();
    } catch (error) {
      console.error("Error resubmitting course:", error);
      toast({
        title: "Error",
        description: "Unable to resubmit course. Please try again.",
        variant: "destructive",
      });
    }
  };

  const courseCount = Array.isArray(approvedCourses) ? approvedCourses.length : 0;
  const activeCourses = approvedCourses.filter(c => c.status === "approved").length;

  const statsData = {
    mentees: 0,
    totalCourses: courseCount,
    activeCourses: activeCourses,
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <div className="w-full bg-cover bg-center h-64 relative" style={{ backgroundImage: "url('/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png')" }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        <div className="container mx-auto px-4 h-full flex items-end pb-8 relative z-10">
          <div>
            <div className="uppercase text-emu-blue font-medium tracking-wide mb-1">
              MENTOR'S
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              My Academy
            </h1>
          </div>
        </div>
      </div>
      
      <main className="flex-grow pt-8 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <AcademyStats 
              mentees={statsData.mentees}
              totalCourses={statsData.totalCourses}
              activeCourses={statsData.activeCourses}
            />
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="courses">My Courses</TabsTrigger>
                <TabsTrigger value="availability">My Availability</TabsTrigger>
              </TabsList>
              
              <TabsContent value="courses" className="mt-6">
                <div className="mb-10">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">My Courses</h2>
                    <CourseCreationForm onCourseCreated={loadApprovedCourses}>
                      <Button variant="action">
                        <BookPlus className="mr-2 h-4 w-4" /> Create New Course
                      </Button>
                    </CourseCreationForm>
                  </div>
                  
                  <CoursesTable 
                    courses={approvedCourses}
                    isLoading={isLoading}
                    onToggleActive={handleToggleActive}
                    onDeleteCourse={handleDeleteCourse}
                    onResubmitCourse={handleResubmitCourse}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="availability" className="mt-6">
                <AvailabilityTab />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      <CourseEditDialog
        course={selectedCourse}
        isOpen={isManageDialogOpen}
        onClose={() => setIsManageDialogOpen(false)}
        onCourseUpdated={loadApprovedCourses}
      />

      <Footer />
    </div>
  );
};

export default MyAcademy;
